import { useEffect, useRef, useState } from "react";
import {
  exportCustomers, getCustomer, getCustomerTiles, listCustomers, saveCustomerState,
  type Customer, type CustomerNote, type CustomerOrder, type CustomerTile,
} from "./actions";
import { downloadCsv } from "./csv";

// Customers (CRM). Built from the EXPORTED store database; it will be synced with the live site later. Every tile is a filter and every number can be checked
// by clicking it. The cleanup decision (keep / review / remove from the clean set) is only a LABEL for the cleanup list: nothing is deleted anywhere.
// Personal data: the page needs the customers capability, and exporting needs its own.

const money = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }));
const money2 = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" }));
const name = (c: Customer) => [c.first_name, c.last_name].filter(Boolean).join(" ") || "(no name)";
const csvCell = (v: unknown) => `"${String(Array.isArray(v) ? v.join("; ") : typeof v === "object" && v ? JSON.stringify(v) : v ?? "").replace(/"/g, '""')}"`;
const PAGE = 50;
const DECISION: Record<string, string> = { keep: "Keep", review: "Review", remove: "Remove" };
const CHIPS: [string, string, string][] = [
  ["flag_attack", "attack", "bad"], ["flag_many_failed", "3+ failed", "bad"], ["flag_proxy", "proxy", "warn"], ["flag_low_score", "low score", "warn"],
  ["flag_refund_heavy", "refund-heavy", "warn"], ["flag_cancel_heavy", "cancel-heavy", "warn"], ["flag_junk", "cleanup", "warn"], ["flag_shared_phone", "shared phone", "warn"],
  ["flag_high_value", "high value", "ok"], ["flag_lapsed", "lapsed", "warn"], ["flag_new", "new", "ok"],
];

function Chips({ c }: { c: Customer }) {
  return <>{CHIPS.filter(([k]) => c[k as `flag_${string}`]).map(([k, label, tone]) => <span key={k} className={`strip-badge ${tone}`} style={{ padding: "1px 8px", marginRight: 4, fontSize: 11.5 }}>{label}</span>)}</>;
}

function Detail({ ckey, canWrite, onClose, onChanged }: { ckey: string; canWrite: boolean; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<{ customer: Customer; orders: CustomerOrder[]; state: { cleanup: string; tags: string[]; follow_up: string; notes: CustomerNote[] } } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");
  const [follow, setFollow] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const r = await getCustomer(ckey);
    if (!r.ok) { setErr(r.error); return; }
    setErr(null); setD(r.data); setTags(r.data.state.tags.join(", ")); setFollow(r.data.state.follow_up || "");
  };
  useEffect(() => { setD(null); setNote(""); load(); }, [ckey]);
  const save = async (patch: Parameters<typeof saveCustomerState>[1]) => {
    setBusy(true);
    const r = await saveCustomerState(ckey, patch);
    setBusy(false);
    if (!r.ok) { setErr(r.error); return; }
    setNote(""); await load(); onChanged();
  };
  if (err && !d) return <aside className="drawer"><div className="drawer-head"><span /><button onClick={onClose}>Close ✕</button></div><div className="panel error">{err}</div></aside>;
  if (!d) return <aside className="drawer"><div className="drawer-head"><span /><button onClick={onClose}>Close ✕</button></div><div className="meta">Loading…</div></aside>;
  const c = d.customer;
  const addr = (a: Record<string, string>) => [a.address_1, a.address_2, a.city, a.state, a.postcode, a.country].filter(Boolean).join(", ") || "—";
  const stat = (label: string, value: string | number) => <div className="tile" style={{ padding: "8px 12px", minWidth: 96 }}><div className="tile-value" style={{ fontSize: 17 }}>{value}</div><div className="tile-label">{label}</div></div>;
  return (
    <aside className="drawer" aria-label="Customer detail">
      <div className="drawer-head">
        <div className="meta" style={{ margin: 0 }}>{c.segment} · {c.rfm_segment}</div>
        <button onClick={onClose} aria-label="Close the detail view">Close ✕</button>
      </div>
      {err && <div className="panel error">{err}</div>}
      <h2 style={{ margin: "6px 0 0" }}>{name(c)}</h2>
      <div className="meta" style={{ margin: "2px 0 8px" }}>{c.company ? c.company + " · " : ""}{c.email}{c.phone ? " · " + c.phone : ""}</div>
      <div style={{ marginBottom: 8 }}><Chips c={c} /></div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {stat("Net revenue", money(c.ltv))}{stat("Orders", c.orders_total)}{stat("Completed", c.orders_completed)}{stat("Avg order", money(c.aov))}
        {stat("Cancelled", c.orders_cancelled)}{stat("Refunded", c.orders_refunded)}{stat("Failed", c.orders_failed)}
        {stat("Days since last", c.days_since_last ?? "—")}{stat("Usual gap (days)", c.median_gap_days ?? "—")}
      </div>

      <h3 style={{ margin: "16px 0 4px" }}>Profile</h3>
      <table className="manual-products"><tbody>
        <tr><td>Account</td><td>{c.registered ? `Registered ${c.user_registered || ""}${c.role && c.role !== "customer" ? " (" + c.role + ")" : ""}` : "Guest (no account)"}{c.last_active ? ` · last active ${c.last_active}` : ""}</td></tr>
        <tr><td>Billing address</td><td>{addr(c.billing)}</td></tr>
        <tr><td>Shipping address</td><td>{addr(c.shipping)}</td></tr>
        <tr><td>First / last order</td><td>{c.first_order || "—"} / {c.last_order || "—"}</td></tr>
        <tr><td>RFM</td><td>recency {c.rfm_r}, frequency {c.rfm_f}, monetary {c.rfm_m} → {c.rfm_segment}</td></tr>
        <tr><td>Paid with</td><td>{c.payment_methods?.join(", ") || "—"}</td></tr>
        <tr><td>Came from</td><td>{c.sources?.join(", ") || "not recorded"}</td></tr>
        <tr><td>Marketing consent</td><td>Unknown: the export carries no consent record. Do not send marketing until it is recorded.</td></tr>
      </tbody></table>

      <h3 style={{ margin: "16px 0 4px" }}>Fraud and cleanup signals</h3>
      <table className="manual-products"><tbody>
        <tr><td>Fraud-plugin score</td><td>{c.min_fraud_score ?? "—"} (lowest across their orders; under 40 is flagged)</td></tr>
        <tr><td>Different IPs used</td><td>{c.distinct_ips}</td></tr>
        <tr><td>Cleanup reasons</td><td>{c.junk_reasons?.length ? c.junk_reasons.join("; ") : "none"}</td></tr>
      </tbody></table>

      <h3 style={{ margin: "16px 0 4px" }}>Cleanup decision</h3>
      <div className="panel">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {["keep", "review", "remove", ""].map((v) => (
            <button key={v || "clear"} type="button" disabled={!canWrite || busy} className={d.state.cleanup === v ? "active" : ""}
                    style={d.state.cleanup === v ? { outline: "2px solid var(--accent)" } : undefined} onClick={() => save({ cleanup: v })}>
              {v ? DECISION[v] : "Clear"}
            </button>
          ))}
        </div>
        <p className="meta" style={{ margin: "6px 0 0" }}>A label for the cleanup list only: it deletes nothing. "Remove" means leave this customer out of the clean set that is synced to the live site later.</p>
      </div>

      <h3 style={{ margin: "16px 0 4px" }}>Tags and follow-up</h3>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input style={{ flex: 1, minWidth: 180 }} placeholder="tags, comma separated" value={tags} disabled={!canWrite} onChange={(e) => setTags(e.target.value)} />
        <input type="date" value={follow} disabled={!canWrite} onChange={(e) => setFollow(e.target.value)} title="Follow-up date" />
        <button type="button" disabled={!canWrite || busy} onClick={() => save({ tags: tags.split(",").map((t) => t.trim()).filter(Boolean), follow_up: follow })}>Save</button>
      </div>

      <h3 style={{ margin: "16px 0 4px" }}>Notes ({d.state.notes.length})</h3>
      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input style={{ flex: 1 }} placeholder="Add a note (support, a call, why they were flagged)…" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" disabled={busy || !note.trim()} onClick={() => save({ note })}>Add note</button>
        </div>
      )}
      {d.state.notes.slice().reverse().map((n, i) => <div key={i} className="panel" style={{ marginBottom: 6 }}><div className="meta" style={{ margin: 0 }}>{n.at.slice(0, 16).replace("T", " ")} · {n.by}</div>{n.text}</div>)}

      <h3 style={{ margin: "16px 0 4px" }}>Orders ({d.orders.length})</h3>
      <table className="manual-products">
        <thead><tr><th>Date</th><th>#</th><th>Status</th><th>Total</th><th>Refund</th><th>Items</th></tr></thead>
        <tbody>
          {d.orders.map((o) => (
            <tr key={o.order_id} title={`${o.payment}${o.source ? " · " + o.source : ""}${o.fraud_score != null ? " · fraud score " + o.fraud_score : ""}${o.proxy ? " · proxy" : ""}`}>
              <td className="mono">{o.date.slice(0, 10)}</td><td className="mono">{o.order_id}</td>
              <td>{o.status.replace("wc-", "")}{o.attack ? " ⚠ attack" : ""}</td><td className="num">{money2(o.total)}</td><td className="num">{o.refunded ? money2(o.refunded) : ""}</td>
              <td className="wrap" style={{ whiteSpace: "normal" }}>{o.items.slice(0, 2).join("; ")}{o.item_count > 2 ? ` +${o.item_count - 2}` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ margin: "16px 0 4px" }}>Products bought</h3>
      <div className="meta" style={{ margin: 0 }}>{c.top_items?.length ? c.top_items.join(" · ") : "Nothing bought."}</div>
      <p className="meta">Recommendations (same brand and category, approved and in stock, not bought yet) come once old orders are matched to the current catalog.</p>
    </aside>
  );
}

export function CustomersPanel({ canWrite, canExport }: { canWrite: boolean; canExport: boolean }) {
  const [tiles, setTiles] = useState<CustomerTile[]>([]);
  const [meta, setMeta] = useState<{ as_of?: string; customers?: number; orders?: number; loaded_at?: string; source?: string }>({});
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Customer[]>([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const cursors = useRef<unknown[]>([undefined]);
  const request = useRef(0);
  const searching = q.length >= 3;

  const loadTiles = async () => {
    const r = await getCustomerTiles();
    if (!r.ok) { setError(r.error); return; }
    setTiles(r.data.tiles); setMeta(r.data.meta);
  };
  useEffect(() => { loadTiles(); }, []);
  useEffect(() => { const t = setTimeout(() => setQ(searchText.trim()), 350); return () => clearTimeout(t); }, [searchText]);

  const loadPage = async (n: number) => {
    const id = ++request.current;
    setLoading(true);
    const r = await listCustomers(filter, searching ? q : "", cursors.current[n], PAGE);
    if (id !== request.current) return;
    setLoading(false);
    if (!r.ok) { setError(r.error); setRows([]); setHasNext(false); return; }
    setError(null); setRows(r.data.rows);
    cursors.current[n + 1] = r.data.nextCursor ?? undefined; cursors.current.length = n + 2;
    setHasNext(!!r.data.nextCursor); setPage(n);
  };
  useEffect(() => { setSelected(null); cursors.current = [undefined]; setPage(0); loadPage(0); }, [filter, q]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.key === "Escape") { setSelected(null); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const i = rows.findIndex((r) => r.customer_key === selected);
        const n = rows[i + (e.key === "ArrowDown" ? 1 : -1)];
        if (n) { e.preventDefault(); setSelected(n.customer_key); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, rows]);

  const doExport = async () => {
    const label = tiles.find((t) => t.key === filter)?.label ?? filter;
    if (!window.confirm(`Export every customer in "${label}" with names, emails, phones and addresses? This is personal data: keep the file private and delete it when finished.`)) return;
    setExporting(true);
    const all: Record<string, unknown>[] = [];
    let cursor: unknown = undefined;
    for (let i = 0; i < 400; i++) {
      const r = await exportCustomers(filter, cursor);
      if (!r.ok) { setError(r.error); break; }
      all.push(...r.data.rows);
      if (!r.data.nextCursor) break;
      cursor = r.data.nextCursor;
    }
    setExporting(false);
    if (all.length) {
      const head = Object.keys(all[0]);
      downloadCsv(`hec-customers-${filter}.csv`, [head, ...all.map((r) => head.map((h) => r[h]))].map((l) => l.map(csvCell).join(",")).join("\r\n"));
    }
  };

  const groups = Array.from(new Set(tiles.map((t) => t.group)));
  const active = tiles.find((t) => t.key === filter);

  return (
    <div className={`sheet-page sheet-split${selected ? " has-detail" : ""}`}>
      <div className="sheet-main">
        <div className="sheet-top">
          <div className="sheet-title">
            <h1>Customers</h1>
            <span className="meta">
              From the exported store database{meta.as_of ? `, orders up to ${meta.as_of}` : ""}{meta.customers ? ` · ${meta.customers.toLocaleString()} customers, ${meta.orders?.toLocaleString()} orders` : ""}; it will be
              synced with the live site later. Click a tile to filter, a row for the detail. Personal data.
            </span>
          </div>
          <div className="tile-groups">
            {groups.map((g) => (
              <div key={g} className="tile-group">
                <span className="tile-group-name">{g}</span>
                {tiles.filter((t) => t.group === g).map((t) => (
                  <div key={t.key} className={["tile clickable", filter === t.key && !searching ? "active" : "", ["Fraud and risk", "Problems"].includes(g) && (t.count ?? 0) > 0 ? "warn" : ""].join(" ").trim()}
                       role="button" tabIndex={0} title={t.label}
                       onClick={() => { setSearchText(""); setQ(""); setFilter(t.key); }}>
                    <div className="tile-value">{t.count == null ? "…" : t.count.toLocaleString()}</div>
                    <div className="tile-label">{t.label}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="sheet-controls">
            <input type="search" className="sheet-search" value={searchText} placeholder="Search email, last name, full name or phone…" onChange={(e) => setSearchText(e.target.value)} aria-label="Search customers"
                   title="Matches the start of an email, last name, full name or phone number" />
            <span className="meta sheet-status">{loading ? "Loading…" : searching ? `${rows.length} match${rows.length === 1 ? "" : "es"} for “${q}”` : `${rows.length} on this page`}{active && !searching ? ` · ${active.label}` : ""}</span>
            {canExport && <button type="button" className="sheet-export" disabled={exporting || searching} onClick={doExport}>{exporting ? "Exporting…" : "Export this list (CSV)"}</button>}
          </div>
          {error && <div className="panel error sheet-error">{error}</div>}
        </div>

        <div className="sheet-scroll">
          <table className="manual-products sheet-table">
            <thead><tr><th title="Cleanup decision">Decision</th><th>Name</th><th>Email</th><th>Phone</th><th>Where</th><th>Segment</th><th>Value group</th><th>Orders</th><th>Done</th><th>Cancel</th><th>Refund</th><th>Failed</th>
              <th>Net revenue</th><th>Avg order</th><th>First order</th><th>Last order</th><th>Signals</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.customer_key} className={selected === c.customer_key ? "selected" : ""} onClick={() => setSelected(c.customer_key)}>
                  <td><span className={`dot dot-${c.crm?.cleanup === "remove" ? "red" : c.crm?.cleanup === "review" ? "orange" : c.crm?.cleanup === "keep" ? "ok" : "none"}`} title={c.crm?.cleanup ? DECISION[c.crm.cleanup] : "no decision"} role="img" aria-label="decision" /></td>
                  <td><span className="clip clip-name" title={name(c)}>{name(c)}</span>{c.company ? <div className="meta" style={{ margin: 0, fontSize: 12 }}>{c.company}</div> : null}</td>
                  <td className="mono">{c.email}</td><td className="mono">{c.phone || "—"}</td>
                  <td>{[c.city, c.state].filter(Boolean).join(", ") || "—"}</td><td>{c.segment}</td><td>{c.rfm_segment === "No completed order" ? "—" : c.rfm_segment}</td>
                  <td className="num">{c.orders_total}</td><td className="num">{c.orders_completed}</td><td className="num">{c.orders_cancelled || ""}</td><td className="num">{c.orders_refunded || ""}</td><td className="num">{c.orders_failed || ""}</td>
                  <td className="num">{money(c.ltv)}</td><td className="num">{c.aov ? money(c.aov) : "—"}</td><td className="mono">{c.first_order || "—"}</td><td className="mono">{c.last_order || "—"}</td>
                  <td><Chips c={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && !loading && !error && <p className="meta" style={{ padding: 16 }}>{tiles.length === 0 ? "No customers loaded yet." : "No customers match."}</p>}
        </div>

        <div className="sheet-pager" role="navigation" aria-label="Pagination">
          <span className="meta">{rows.length ? `Rows ${(page * PAGE + 1).toLocaleString()}–${(page * PAGE + rows.length).toLocaleString()}` : "No rows"}{active?.count != null && !searching ? ` of ${active.count.toLocaleString()}` : ""} · Page {page + 1}</span>
          <span className="sheet-pager-buttons">
            <button type="button" disabled={loading || page === 0 || searching} onClick={() => loadPage(0)}>First</button>
            <button type="button" disabled={loading || page === 0 || searching} onClick={() => loadPage(page - 1)}>Previous</button>
            <button type="button" disabled={loading || !hasNext || searching} onClick={() => loadPage(page + 1)}>Next</button>
          </span>
        </div>
      </div>
      {selected && <Detail ckey={selected} canWrite={canWrite} onClose={() => setSelected(null)} onChanged={() => { loadTiles(); loadPage(page); }} />}
    </div>
  );
}
